/**
 * ContextBlock — Sub-fatia 10.0 PR 3 — renderiza payload composto do backend.
 *
 * Suporta payload com `externalSources?` OPCIONAL:
 *   - Sem campo → tenant sem capability external-context-sync → renderiza
 *     apenas linhas internal + rag (regra do silêncio: zero menção a conector).
 *   - Com campo + ao menos um conector com status degraded/unreachable/etc →
 *     banner laranja com displayName do conector.
 *   - Com campo + todos normal → linhas com source-tag dinâmica.
 */

import { channelLabel } from '../../format';

import type { CustomerContextResponse } from '../../../../lib/api-client';
import type { ChannelKind } from '@lume/shared';

export interface ContextBlockProps {
  context: CustomerContextResponse;
  identity: { channel: ChannelKind; channelIdentifier: string } | null;
  channel: ChannelKind;
  onRefresh?: (() => void) | undefined;
  refreshing?: boolean | undefined;
}

export function ContextBlock({
  context,
  identity,
  channel,
  onRefresh,
  refreshing = false,
}: ContextBlockProps): JSX.Element {
  const degraded = context.externalSources?.find((s) =>
    ['degraded', 'unreachable', 'invalid-response', 'business-error'].includes(s.status),
  );

  const hasActiveConnector =
    context.externalSources !== undefined && context.externalSources.length > 0;

  return (
    <div className="context-block">
      {degraded !== undefined && (
        <p className="context-block__degraded" role="alert">
          ⚠ {degraded.displayName}{' '}
          {degraded.status === 'degraded'
            ? 'degradado · dados podem estar desatualizados'
            : degraded.status === 'unreachable'
              ? 'indisponível · usando cache local quando há'
              : degraded.status === 'business-error'
                ? // A-42 (noturna 04/Set/2026): pessoa que não existe no ERP não é
                  // «dado inválido» do sistema externo — é cliente não encontrado.
                  'não encontrou este cliente · sem dados do sistema externo'
                : 'devolveu dados que a Lume não reconheceu'}
        </p>
      )}

      <dl className="context-block">
        <ContextRow
          source="internal"
          sourceDisplayName="Lume"
          label="Canal"
          value={
            identity !== null
              ? `${channelLabel(identity.channel)} · ${identity.channelIdentifier}`
              : channelLabel(channel)
          }
        />
        {context.rows.map((r) => (
          <ContextRow
            key={`${r.source}-${r.label}`}
            source={r.source}
            sourceDisplayName={r.sourceDisplayName}
            label={r.label}
            value={r.value}
          />
        ))}
      </dl>

      {hasActiveConnector && onRefresh !== undefined && (
        <button
          type="button"
          className="btn btn--sm btn--ghost context-block__refresh"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing
            ? 'Atualizando...'
            : `Atualizar do ${context.externalSources?.length === 1 ? (context.externalSources[0]?.displayName ?? 'conector') : 'conector'}`}
        </button>
      )}
    </div>
  );
}

function ContextRow({
  source: _source,
  sourceDisplayName,
  label,
  value,
}: {
  source: string;
  sourceDisplayName: string;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="context-block__row">
      <dt>
        <span className="context-block__source-tag">{sourceDisplayName}</span> {label}
      </dt>
      <dd>{value}</dd>
    </div>
  );
}
